import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiGlobe, FiCheck } from 'react-icons/fi';
import { Dropdown } from '@/components/Dropdown';
import { Button, buttonVariants } from '@/components/Button';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { addNotification, notificationStates } from '@/components/Notification';
import { useTimezones } from '@/hooks/useTimezones';
import { teamActionTypes } from '@/store/Team/Team.actionTypes';
import teamDatabase from '@/services/team';
import styles from './Profile.module.scss';

const dropdownStyles = {
  valueContainer: { height: '34px' },
  control: { height: '34px' },
};

export const TeamSettingsTab = () => {
  const dispatch = useDispatch();

  const teamsData = useSelector((s) => s.team?.teams?.data ?? []);
  const activeTeamId = useSelector((s) => s.team?.active?.activeTeamid);

  const teamOptions = useMemo(
    () =>
      teamsData
        .filter((t) => !t.teamRemoved)
        .map((t) => ({ ...t, value: t.activeTeamid, label: t.Team })),
    [teamsData]
  );

  const timezoneOptions = useTimezones();

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Auto-select the currently viewing team on mount
  useEffect(() => {
    if (!activeTeamId || !teamOptions.length) return;
    const match = teamOptions.find((t) => t.value === activeTeamId);
    if (match) setSelectedTeam(match);
  }, [activeTeamId, teamOptions]);

  // Sync timezone dropdown when selected team changes
  useEffect(() => {
    if (!selectedTeam) {
      setSelectedTimezone(null);
      return;
    }
    const tz = selectedTeam.timeZone ?? null;
    setSelectedTimezone(tz ? { value: tz, label: tz } : null);
  }, [selectedTeam?.value]);

  const savedTimezone = selectedTeam?.timeZone ?? null;

  const hasChanges =
    !!selectedTeam?.value &&
    !!selectedTimezone?.value &&
    selectedTimezone.value !== savedTimezone;

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;
    setIsSaving(true);

    const res = await teamDatabase.updateTeamTimezone(
      selectedTeam.value,
      selectedTimezone.value
    );

    if (res.success) {
      dispatch({
        type: teamActionTypes.PATCH_TEAM_TIMEZONE,
        payload: {
          teamId: selectedTeam.value,
          timeZone: selectedTimezone.value,
        },
      });
      addNotification({
        message: `Timezone saved for "${selectedTeam.label}"`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to save timezone. Please try again.',
        level: notificationStates.error,
      });
    }

    setIsSaving(false);
  };

  return (
    <div className={styles.teamSettingsTab}>
      <div className={styles.profileTitleContainer}>
        <Text
          variant={textVariantsEnum.Text_xl}
          fontWeight={fontWeightEnum.Bold}
          color="primary-500"
        >
          Team Settings
        </Text>
      </div>

      <div className={styles.teamSettingsBody}>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          Configure settings for each team. Select a team to view and update its
          configuration.
        </Text>

        <div className={styles.teamSettingsCard}>
          <div className={styles.teamSettingsCardHeader}>
            <FiGlobe size={18} color="#3363F5" />
            <Text
              variant={textVariantsEnum.Text_md}
              fontWeight={fontWeightEnum.Semibold}
              color="primary-500"
            >
              Timezone Configuration
            </Text>
          </div>

          <p className={styles.teamSettingsCardDescription}>
            The team timezone defines the day boundary used for attendance,
            productivity tracking, and reports. All members&apos; data is
            consolidated against this timezone.
          </p>

          <div className={styles.teamSettingsFields}>
            <div className={styles.teamSettingsField}>
              <Dropdown
                label="Team"
                options={teamOptions}
                value={selectedTeam}
                width="100%"
                placeholder="Select a team"
                onChange={setSelectedTeam}
                selectStyles={dropdownStyles}
              />
            </div>

            <div className={styles.teamSettingsField}>
              <Dropdown
                label="Timezone"
                options={timezoneOptions}
                value={selectedTimezone}
                width="100%"
                placeholder="Select timezone"
                onChange={setSelectedTimezone}
                selectStyles={dropdownStyles}
                isDisabled={!selectedTeam}
              />
              {savedTimezone && (
                <span className={styles.teamSettingsSavedHint}>
                  <FiCheck size={12} />
                  Currently saved: {savedTimezone}
                </span>
              )}
              {selectedTeam && !savedTimezone && (
                <span className={styles.teamSettingsNoTzHint}>
                  No timezone set — choose one and save.
                </span>
              )}
            </div>
          </div>

          <div className={styles.teamSettingsActions}>
            <Button
              text={isSaving ? 'Saving…' : 'Save Timezone'}
              variant={buttonVariants.primary}
              disabled={!hasChanges || isSaving}
              onClick={handleSave}
              style={{ backgroundColor: hasChanges ? '#3363F5' : undefined }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
